import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductsSiteLayout from '../../../LandingPage/products/ProductsSiteLayout';
import { useProductsTheme } from '../../../LandingPage/products/useProductsTheme';
import { useSessionAuth } from '../../../utils/SessionAuthContext';
import { peekSessionCache } from '../../../utils/sessionAuth';
import { storageBoxApi } from '../../../storageBox/api/storageBoxApi';
import { STORAGE_BOX_ROOT_LABEL } from '../../../storageBox/constants';
import StorageBoxNewTextFileModal from '../../../storageBox/components/StorageBoxNewTextFileModal';
import { commentBodyIsEffectivelyEmpty } from './textEditorBodyUtils';
import '../../../storageBox/StorageBoxPage.css';
import './TextEditorProductPage.css';

const GUEST_DRAFT_STORAGE_KEY = 'bahlus-text-editor-guest-draft';
const GUEST_SAVE_LOCATION = 'Text Editor · This device';

export default function TextEditorProductPage() {
  useProductsTheme();
  const navigate = useNavigate();
  const { status } = useSessionAuth();
  const authed = status === 'authenticated' || peekSessionCache();
  const [editorKey] = useState(() => Date.now());
  const [editorOpen, setEditorOpen] = useState(true);
  const [busy, setBusy] = useState(false);

  const saveLocationPath = useMemo(
    () => (authed ? STORAGE_BOX_ROOT_LABEL : GUEST_SAVE_LOCATION),
    [authed]
  );

  useLayoutEffect(() => {
    const header = document.querySelector('.products-sticky-header');
    const sync = () => {
      const bottom =
        header instanceof HTMLElement ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      document.documentElement.style.setProperty('--products-sticky-header-height', `${bottom}px`);
    };
    sync();
    window.addEventListener('resize', sync, { passive: true });
    return () => {
      window.removeEventListener('resize', sync);
      document.documentElement.style.removeProperty('--products-sticky-header-height');
    };
  }, []);

  const handleClose = useCallback(() => {
    setEditorOpen(false);
    navigate('/products');
  }, [navigate]);

  const handleSave = useCallback(
    async (file, normalizedName, options) => {
      setBusy(true);
      try {
        const text = await file.text();
        if (commentBodyIsEffectivelyEmpty(text)) return;

        if (authed) {
          await storageBoxApi.uploadFiles(null, [file], null);
          if (!options?.saveAs) {
            setEditorOpen(false);
            navigate('/products');
          }
          return;
        }

        localStorage.setItem(
          GUEST_DRAFT_STORAGE_KEY,
          JSON.stringify({
            name: normalizedName || file.name,
            text,
            savedAt: Date.now(),
          })
        );
      } finally {
        setBusy(false);
      }
    },
    [authed, navigate]
  );

  if (!editorOpen) {
    return (
      <ProductsSiteLayout activeNav="products" mainClassName="text-editor-product-page-route">
        {null}
      </ProductsSiteLayout>
    );
  }

  return (
    <ProductsSiteLayout activeNav="products" mainClassName="text-editor-product-page-route">
      <StorageBoxNewTextFileModal
        isOpen
        kind="rich"
        editorKey={editorKey}
        busy={busy}
        onClose={handleClose}
        onSave={handleSave}
        saveLocationPath={saveLocationPath}
        fileMenuUserLoggedIn={authed}
      />
    </ProductsSiteLayout>
  );
}
